<script lang="ts">
	import { avatarGlyph, avatarIsLetter, avatarTint, isDicebearAvatar, dicebearUrl } from '$lib/avatars';

	let {
		avatar,
		username = '',
		size = 8,
		extraClass = '',
		style = '',
		slim = false,
		title = ''
	}: {
		avatar: string;
		username?: string;
		/** Tailwind size scale (e.g. 8 → 2rem). */
		size?: number;
		/** Extra classes for theme hooks (player-row-avatar, coop-avatar, bottomnav-avatar, …). */
		extraClass?: string;
		/** Inline style passthrough (margin/z-index/opacity for stacked avatars). */
		style?: string;
		/** Slim 1px frame variant (the co-op You/partner chip). */
		slim?: boolean;
		title?: string;
	} = $props();

	let rem = $derived(size / 4);
	let isImg = $derived(isDicebearAvatar(avatar));
	let src = $derived(isImg ? dicebearUrl(avatar) : '');
	let tint = $derived(avatarTint(avatar, username));

	// If a DiceBear SVG fails to load (the public API rate-limits bursts),
	// fall back to the username initial instead of a broken-image icon.
	let imgFailed = $state(false);
	$effect(() => {
		// Reset whenever the source changes so a new avatar gets a fresh try.
		src;
		imgFailed = false;
	});

	let showImg = $derived(isImg && !imgFailed);
	let glyph = $derived(isImg ? (username || '?').charAt(0).toUpperCase() : avatarGlyph(avatar, username));
	let isLetter = $derived(isImg ? true : avatarIsLetter(avatar, username));
	// Single consistent ratio so every avatar renders the glyph the same way.
	let glyphFont = $derived(`${(isLetter ? 0.5 : 0.6) * rem}rem`);
</script>

<div
	class="pixel-frame relative {extraClass}"
	style="width: {rem}rem; height: {rem}rem; --avatar-tint: {tint};{style ? ` ${style}` : ''}"
	{title}
>
	<div class="pixel-frame absolute inset-0 bg-(--guess-frame-outer)"></div>
	{#if slim}
		<div class="pixel-frame absolute inset-px overflow-hidden bg-(--guess-frame-fill)" style:background={tint}>
			{#if showImg}
				<img {src} alt={username} loading="lazy" decoding="async" onerror={() => (imgFailed = true)} class="absolute inset-0 h-full w-full object-cover" />
			{/if}
		</div>
	{:else}
		<div class="pixel-frame absolute inset-0.5 bg-(--guess-frame-highlight)"></div>
		<div class="pixel-frame absolute inset-1 bg-(--guess-frame-shadow)"></div>
		<div class="pixel-frame absolute inset-1.5 overflow-hidden bg-(--guess-frame-fill)" style:background={tint}>
			{#if showImg}
				<img {src} alt={username} loading="lazy" decoding="async" onerror={() => (imgFailed = true)} class="absolute inset-0 h-full w-full object-cover" />
			{/if}
		</div>
	{/if}
	<div class="absolute inset-0 flex items-center justify-center text-(--guess-frame-text)">
		{#if !showImg}
			<span
				class="uppercase leading-none"
				style="font-size: {glyphFont};{isLetter ? ' color: var(--avatar-ink); font-weight: 700 !important;' : ''}"
			>{glyph}</span>
		{/if}
	</div>
</div>
